import {Subscription} from 'rxjs';

import IPlayer               from '../../Player/Interfaces/IPlayer';
import {VERSION}             from '../../Shared/Constants/Build';
import logger                from '../../Shared/Util/logger';
import inferAbrFormatFromSrc from '../../Solutions/Shared/Util/inferAbrFormatFromSrc';
import inferDrmTypeFromSrc   from '../../Solutions/Shared/Util/inferDrmTypeFromSrc';
import Config                from '../../State/Stores/Config/Config';
import Media                 from '../../State/Stores/Media/Media';
import IAnalytics            from '../Shared/Interfaces/IAnalytics';

import connectAnalyticsSubscriptions from './connectAnalyticsSubscriptions';
import Conviva                       from './Vendor/Conviva';
import Html5Http                     from './Vendor/Html5Http';
import Html5Logging                  from './Vendor/Html5Logging';
import Html5Metadata                 from './Vendor/Html5Metadata';
import Html5Storage                  from './Vendor/Html5Storage';
import Html5Time                     from './Vendor/Html5Time';
import Html5Timer                    from './Vendor/Html5Timer';

class ConvivaAnalytics implements IAnalytics {
    private player: IPlayer;

    private systemFactory: Conviva.SystemFactory;
    private client: Conviva.Client = null;
    private playerStateManager: Conviva.PlayerStateManager;
    private sessionKey: number = Conviva.Client.NO_SESSION_KEY;
    private subscriptions: Subscription[] = [];
    private isSessionDataFullyPopulated: boolean = false;

    constructor(player: IPlayer) {
        this.player = player;

        const {config} = player.state.stores;
        const analyticsConfig = config.value.analytics;

        if (!analyticsConfig.enable || !analyticsConfig.convivaCustomerKey) return;

        logger.log('[Conviva] Analytics enabled');

        const systemInterface = new Conviva.SystemInterface(
            new Html5Time(),
            new Html5Timer(),
            new Html5Http(),
            new Html5Storage(),
            new Html5Metadata(),
            new Html5Logging()
        );

        const systemSettings = new Conviva.SystemSettings();
        const clientSettings = new Conviva.ClientSettings(analyticsConfig.convivaCustomerKey);

        this.systemFactory = new Conviva.SystemFactory(systemInterface, systemSettings);

        if (analyticsConfig.convivaGatewayUrl !== '') {
            clientSettings.gatewayUrl = analyticsConfig.convivaGatewayUrl;
        }

        this.client = new Conviva.Client(clientSettings, this.systemFactory);
        this.playerStateManager = this.client.getPlayerStateManager();

        this.subscriptions.push(...connectAnalyticsSubscriptions(
            this,
            this.player,
            () => this.playerStateManager,
            this.client
        ));
    }

    public get isEnabled(): boolean {
        return this.client !== null;
    }

    private get isSessionActive(): boolean {
        return this.sessionKey !== Conviva.Client.NO_SESSION_KEY;
    }

    private get isPlayerStateManagerActive(): boolean {
        // @ts-ignore
        return this.playerStateManager !== null && !this.playerStateManager._released;
    }

    private get playerType(): string {
        return `[DAZN 2.0]${this.player.solution ? ' ' + this.player.solution.type : ''}`;
    }

    public resolveSessionKey(): number {
        return this.sessionKey;
    }

    public destroy(): void {
        if (this.isSessionActive) {
            this.endSession();
        }

        if (this.isEnabled) {
            this.client.release();
            this.systemFactory.release();
        }

        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().unsubscribe();
        }
    }

    public startSession(): void {
        if (this.isSessionActive) return;

        const {media, config} = this.player.state.stores;

        const contentMetadata = ConvivaAnalytics.createContentMetadata(
            media.value,
            config.value,
            this.playerType,
            this.player.solution.type,
            this.player.solution.version
        );

        this.sessionKey = this.client.createSession(contentMetadata);

        logger.log(`[Conviva] Analytics session created with ID: ${this.client.getId()} and key: ${this.sessionKey}`);
        logger.log(`[Conviva]     viewerId: ${contentMetadata.viewerId}`);
        logger.log(`[Conviva]     assetName: ${contentMetadata.assetName}`);
        logger.log(`[Conviva]     custom: ${JSON.stringify(contentMetadata.custom)}`);
        logger.log(`[Conviva]     streamType: ${contentMetadata.streamType}`);
        logger.log(`[Conviva]     streamUrl: ${media.value.src}`);
        logger.log(`[Conviva]     playerType: ${this.playerType}`);

        if (!this.isPlayerStateManagerActive) {
            this.playerStateManager = this.client.getPlayerStateManager();
        }

        this.playerStateManager.setStreamUrl(media.value.src);
        this.playerStateManager.setPlayerType(this.playerType);
        this.playerStateManager.setPlayerState(Conviva.PlayerStateManager.PlayerState.BUFFERING);

        logger.log(`[Conviva] Setting player state to ${Conviva.PlayerStateManager.PlayerState.BUFFERING}`);

        this.client.attachPlayer(this.sessionKey, this.playerStateManager);
    }

    /**
     * Updates the session with any data only available once the stream has
     * been fully loaded and is playable.
     */

    public updateSession(): void {
        if (this.isSessionDataFullyPopulated || !this.isPlayerStateManagerActive) return;

        const {playback} = this.player.state.stores;

        if (!playback.value.media.isLinear) {
            logger.log(`[Conviva] Updating with session with:`);
            logger.log(`[Conviva]     duration: ${playback.value.duration}`);

            this.playerStateManager.setDuration(playback.value.duration);
        }

        this.isSessionDataFullyPopulated = true;
    }

    public endSession(): void {
        if (!this.isSessionActive) return;

        logger.log(`[Conviva] Ending session with ID: ${this.client.getId()} and key: ${this.sessionKey}`);

        this.client.detachPlayer(this.sessionKey);
        this.client.releasePlayerStateManager(this.playerStateManager);
        this.client.cleanupSession(this.sessionKey);

        this.sessionKey = Conviva.Client.NO_SESSION_KEY;
        this.isSessionDataFullyPopulated = false;
    }

    private static createContentMetadata(
        media: Media,
        config: Config,
        playerType: string,
        playerSolutionType: string,
        playerSolutionVersion: string
    ): Conviva.ContentMetadata {
        const contentMetadata = new Conviva.ContentMetadata();

        contentMetadata.applicationName = playerType;
        contentMetadata.viewerId = config.analytics.viewerId;
        contentMetadata.assetName = media.title;
        contentMetadata.custom = media.convivaCustomMetaData;
        contentMetadata.applicationName = playerType;

        const applicationVersion = contentMetadata.custom.applicationVersion || '';

        contentMetadata.custom.drmType = inferDrmTypeFromSrc(media.src);
        contentMetadata.custom.streamingProtocol = inferAbrFormatFromSrc(media.src);
        contentMetadata.custom.applicationVersion = applicationVersion ? applicationVersion + `/${VERSION}` : VERSION;
        contentMetadata.custom.applicationType = playerType;
        contentMetadata.custom.nativePlayerVendor =
            playerSolutionType ? playerSolutionType : 'Unknown nativePlayerVendor';
        contentMetadata.custom.nativePlayerVersion =
            playerSolutionVersion ? playerSolutionVersion : 'Unknown nativePlayerVersion';

        contentMetadata.streamType = media.isLiveOrLinear ?
            Conviva.ContentMetadata.StreamType.LIVE :
            Conviva.ContentMetadata.StreamType.VOD;

        return contentMetadata;
    }
}

export default ConvivaAnalytics;